import { existsFilter } from '@iotv/core-services'
import { MetricStatsType, NumNull } from '@iotv/iotv-v3-types'

export const stdDev = (values: (number | undefined | null)[]) => {
  var avg = mean(values)

  var squareDiffs = values
    .filter((x) => x && x !== null)
    .map(function (x) {
      if (avg != undefined) {
        var diff = (x as number) - avg
        var sqrDiff = diff * diff
        return sqrDiff
      } else {
        return undefined
      }
    })

  var avgSquareSum = squareDiffs.reduce((a, b) => (a as number) + (b as number), 0)
  var counts = squareDiffs.length
  if (avgSquareSum != undefined && counts != 0) {
    if (counts > 1) {
      var avgSquareDiff: number | undefined = avgSquareSum / (counts - 1)
    } else {
      var avgSquareDiff: number | undefined = 0
    }
  } else {
    var avgSquareDiff: number | undefined = undefined
  }

  if (avgSquareDiff != undefined) {
    var stdDev = Math.sqrt(avgSquareDiff)
    return stdDev
  } else {
    return undefined
  }
}

export const mean = (values: (number | undefined | null)[]) => {
  var sum = values
    .filter((x) => x && x !== null && typeof x === 'number')
    .reduce(function (sum, value, i) {
      const newSum = (sum as number) + (value as number)
      if (isNaN(newSum)) {
        throw new Error(`NaN summed with ${value}`)
      }
      if (newSum === Infinity) {
        throw new Error(`Infinity summed from: ${value} added to ${sum} (at index ${i})`)
      }
      return newSum
    }, 0)

  let cnt = count(values)
  if (cnt > 0) {
    var avg = (sum as number) / cnt
    return avg
  } else {
    return undefined
  }
}

export const count = (values: (number | undefined | null)[]) => values.filter((x) => x && x !== null).length

export const meanStdDevCnt = (values: (number | undefined | null)[]) => {
  return {
    mean: mean(values),
    stdDev: stdDev(values),
    cnt: count(values),
  }
}

export const confidence = (values: (number | undefined | null)[]) => {
  const cnt = count(values)
  var avg = mean(values)

  var squareDiffs = values.map(function (x) {
    if ((x || x === 0) && avg != undefined) {
      var diff = (x as number) - avg
      var sqrDiff = diff * diff
      return sqrDiff
    } else {
      return undefined
    }
  })

  const filteredSquareDiffs = squareDiffs.filter((x) => typeof x === 'number') as number[]

  const totalSquareDiffs = filteredSquareDiffs.reduce((acc: number, curr: number) => acc + curr)

  const confidence = squareDiffs.map((sq, i) => (sq ? Math.sqrt(totalSquareDiffs / sq) : null))

  return confidence
}

export const wagMean = (values: (number | undefined | null)[]) => {
  const confidenceArr = confidence(values)
  const confidenceTot = confidenceArr.reduce((acc: number, curr, i) => acc + (typeof curr === 'number' ? curr : 0), 0 as number)

  const wag =
    confidenceTot !== 0
      ? values.reduce((acc: number, curr, i) => {
          const confidence = confidenceArr[i]
          return typeof curr === 'number' && typeof confidence === 'number' ? acc + curr * confidence : acc
        }, 0) / confidenceTot
      : 0

  return wag
}

type NumberArrayToMetricStatsType = { numNulls: NumNull[]; period?: { firstTimestamp: number; lastTimestamp: number } }

export const numberArrayToMetricStatsType = ({ numNulls, period }: NumberArrayToMetricStatsType): MetricStatsType => {
  const nonNulls = numNulls.filter(existsFilter) as number[]
  const { firstTimestamp = 0, lastTimestamp = 0 } = period ?? {}
  const { cnt, mean = NaN, stdDev } = meanStdDevCnt(nonNulls)
  const result: MetricStatsType = {
    count: cnt,
    max: Math.max(...nonNulls),
    min: Math.min(...nonNulls),
    mean,
    sdPop: stdDev ?? NaN,
    sdSample: stdDev ?? NaN, // TODO!!
    varPop: stdDev ? Math.pow(2, stdDev) : NaN,
    varSample: stdDev ? Math.pow(2, stdDev) : NaN,
    firstTimestamp,
    lastTimeStamp: lastTimestamp,
  }

  return result
}
